import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Checkbox as MuiCheckbox,
  Grid,
  Link,
  Menu as MuiMenu,
  MenuItem as MuiMenuItem,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { FilterList as FilterIcon } from "@mui/icons-material";
import { useFormikContext } from "formik";

import useDownload from "components/shared/Download";
import {
  Para12Text,
  Para16Text,
  ParaSmall,
} from "components/shared/Typography";
import { KITS } from "helpers/apis";
import useCatalogDetail from "hooks/useCatalogDetail";
import { colors } from "theme";
import { PlatformType } from "types/design";
import usePreference from "hooks/usePreference";
import { ICustomPanelForm } from "types/form";

import { IlluminaKit } from "./IlluminaKit";
import { IonTorrentKit } from "./IonTorrentKit";
import { SequencingKit as IKit } from "./type";

interface DeletableSequencingKit extends IKit {
  deleted?: boolean;
}

interface SequencingKitsProps {
  kits: IKit[];
  platformType: PlatformType;
  reads: number;
}

export const SequencingKits: React.FC<SequencingKitsProps> = (props) => {
  const [kits, setKits] = useState<DeletableSequencingKit[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { isDraftCustomPanel, customize, draftSaved, catalog } =
    useCatalogDetail();
  const { currentKits } = usePreference();
  const { values } = useFormikContext<ICustomPanelForm>();

  const visibleKits = useMemo(() => kits.filter((kit) => !kit.deleted), [kits]);
  const kitSettingObj: Record<string, boolean> = useMemo(() => {
    let settings: Record<string, boolean> = {};

    currentKits.forEach((kit) => {
      settings[kit.name!] = !kit.isActive;
    });

    return settings;
  }, [currentKits]);

  const { download, loading: isDownloading } = useDownload({
    requestConfig: { url: `${KITS}export` },
  });

  const footnote = useMemo(() => {
    if (props.platformType === PlatformType.ILLUMINA) {
      return (
        <>
          + In addition to sequencer read capacity, the maximum number of
          samples that can be multiplexed may depend on sample index diversity
          available to the user.
          <br />
          *Corrected total reads are calculated by subtracting PhiX control
          library dedicated reads recommended when using lyophilized Archer® MBC
          Adapters.
        </>
      );
    } else {
      return (
        <>
          + In addition to sequencer read capacity, the maximum number of
          samples that can be multiplexed may depend on sample index diversity
          available to the user.
        </>
      );
    }
  }, [props.platformType]);
  const isFiltered = useMemo(
    () =>
      kits.filter((kit) => kit.deleted !== kitSettingObj[kit.name]).length > 0,
    [kits, kitSettingObj]
  );

  useEffect(() => {
    setKits([
      ...props.kits.map((kit) => ({
        ...kit,
        deleted: kitSettingObj[kit.name],
      })),
    ]);
  }, [props.kits, kitSettingObj]);

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onFilter = useCallback((kitId: number) => {
    setKits((kits) => [
      ...kits.map((kit) => {
        if (kit.id === kitId) {
          return { ...kit, deleted: !kit.deleted };
        } else return kit;
      }),
    ]);
  }, []);

  const resetFilter = () => {
    setKits((kits) => [
      ...kits.map((kit) => ({ ...kit, deleted: kitSettingObj[kit.name] })),
    ]);
  };

  const exportCSV = () => {
    const designName = customize
      ? values.panelName
      : isDraftCustomPanel
      ? draftSaved?.name
      : catalog?.name;

    download({
      params: {
        designName,
        kitIds: visibleKits.map((kit) => kit.id).join(","),
        reads: props.reads,
        platformType: props.platformType,
      },
    });
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap="16px"
      className="sequencing-kits"
      padding={2}
      width={1}
      boxSizing="border-box"
    >
      <Typography variant="h5">
        {props.platformType === PlatformType.ILLUMINA
          ? "Illumina"
          : "Ion Torrent"}
      </Typography>
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            size="small"
            startIcon={<FilterIcon />}
            style={{ textTransform: "none" }}
            onClick={openMenu}
          >
            Filter
          </Button>
          {isFiltered && (
            <Box marginLeft={1.25}>
              <Link
                component="button"
                type="button"
                color="secondary"
                onClick={resetFilter}
              >
                <Para16Text fontWeight={600}>Reset</Para16Text>
              </Link>
            </Box>
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          style={{ textTransform: "none" }}
          onClick={exportCSV}
          disabled={isDownloading}
        >
          Export
        </Button>
      </Box>
      <Grid container spacing={2} alignItems="stretch" component="ul">
        {visibleKits.map((kit) => (
          <Grid item lg={3} md={4} sm={6} xs={12} key={kit.id} component="li">
            {props.platformType === PlatformType.ILLUMINA ? (
              <IlluminaKit kit={kit} onDelete={onFilter} />
            ) : (
              <IonTorrentKit kit={kit} onDelete={onFilter} />
            )}
          </Grid>
        ))}
      </Grid>
      <ParaSmall>{footnote}</ParaSmall>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        elevation={1}
        MenuListProps={{ disablePadding: true }}
        onClose={() => setAnchorEl(null)}
        onClick={() => setAnchorEl(null)}
      >
        {kits.map((kit) => (
          <MenuItem
            key={kit.id}
            dense
            divider
            onClick={(evt: React.MouseEvent) => {
              evt.stopPropagation();
              onFilter(kit.id);
            }}
          >
            <Para12Text>
              <Checkbox color="primary" checked={!kit.deleted} />
              {kit.name}
            </Para12Text>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

const Menu = styled(MuiMenu)({
  list: {
    border: `1px solid ${colors.grey[500]}`,
    borderRadius: 4,
  },
});

const MenuItem = styled(MuiMenuItem)({
  padding: "4px 16px",
});

const Checkbox = styled(MuiCheckbox)({
  padding: 0,
  marginRight: 8,
});
